import {lazy, Suspense, useEffect, type ReactNode} from 'react';
import {createBrowserRouter, Navigate, Outlet} from 'react-router-dom';
import {onForcedLogout} from '@/api/client';
import {FullScreenSpinner} from '@/components/ui';
import SignInPage from '@/pages/sign-in';
import {useAuthStore} from '@/stores/auth';

const HomePage = lazy(() => import('@/pages/home'));

const withSuspense = (node: ReactNode) => (
  <Suspense fallback={<FullScreenSpinner />}>{node}</Suspense>
);

/** Restores the session once, and drops it when the refresh token dies. */
function AuthGate() {
  const restore = useAuthStore((s) => s.restore);
  useEffect(() => {
    void restore();
    return onForcedLogout(() => useAuthStore.getState().setUser(null));
  }, [restore]);
  return <Outlet />;
}

function RequireAuth() {
  const status = useAuthStore((s) => s.status);
  if (status === 'loading') return <FullScreenSpinner />;
  if (status === 'anonymous') return <Navigate to="/sign-in" replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    element: <AuthGate />,
    children: [
      {path: '/sign-in', element: <SignInPage />},
      {
        element: <RequireAuth />,
        children: [{path: '/', element: withSuspense(<HomePage />)}]
      },
      {path: '*', element: <Navigate to="/" replace />}
    ]
  }
]);
